/**
 * SchedulePromptBar.tsx
 * The Intent Capture Component — A focused natural language input
 * that highlights scheduling tokens in real-time.
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Icons } from '../../../components/ui/Icons';
import { highlightScheduleTokens, stripScheduleCommand } from '../../../services/scheduleParser';
import { ParsedScheduleToken } from '../../../types';

interface SchedulePromptBarProps {
  onSubmit: (text: string) => void;
  onCancel: () => void;
  isLoading?: boolean;
  initialValue?: string;
}

const TOKEN_COLORS: Record<ParsedScheduleToken['type'], string> = {
  trigger: 'text-black bg-gray-100 dark:bg-gray-800 dark:text-white',
  action: 'text-black bg-gray-200 dark:bg-gray-700 dark:text-white',
  target: 'text-black bg-gray-300 dark:bg-gray-650 dark:text-white',
  parameter: 'text-black bg-gray-100 dark:bg-gray-800 dark:text-white',
  plain: '',
};

const SchedulePromptBar: React.FC<SchedulePromptBarProps> = ({
  onSubmit,
  onCancel,
  isLoading = false,
  initialValue = '',
}) => {
  const safeInitialValue = typeof initialValue === 'string' ? initialValue : '';
  const [value, setValue] = useState(stripScheduleCommand(safeInitialValue));
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);

  // Real-time token highlighting
  const tokens = useMemo(() => highlightScheduleTokens(value), [value]);
  const hasTokens = tokens.some(t => t.type !== 'plain');

  // Auto-focus on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Auto-resize textarea
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
    }
  }, [value]);

  // Sync scroll between textarea and highlight overlay
  const handleScroll = () => {
    if (highlightRef.current && inputRef.current) {
      highlightRef.current.scrollTop = inputRef.current.scrollTop;
      highlightRef.current.scrollLeft = inputRef.current.scrollLeft;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (value.trim() && !isLoading) {
      onSubmit(value.trim());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as any);
    }
    if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <div className="w-full animate-in" id="schedule-prompt-bar">
      {/* Header Label */}
      <div className="flex items-center gap-2 mb-3 px-1">
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-50 dark:bg-[#171717] border border-slate-200 dark:border-gray-800">
          <Icons.CalendarClock className="w-4 h-4 text-slate-700 dark:text-slate-350" />
          <span className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">New Schedule</span>
        </div>
        {hasTokens && (
          <span className="text-xs text-gray-400 dark:text-gray-500 animate-in">
            AI is parsing your schedule...
          </span>
        )}
      </div>

      {/* Input Card */}
      <form onSubmit={handleSubmit}>
        <div className="relative group transition-all duration-300 rounded-[20px] border border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-lg focus-within:shadow-lg bg-white dark:bg-[#212121] overflow-hidden focus-within:border-black dark:focus-within:border-white">
          
          {/* Highlight Overlay (behind textarea) */}
          <div className="relative">
            <div
              ref={highlightRef}
              className="absolute inset-0 pointer-events-none prompt-textarea-shared"
              aria-hidden="true"
            >
              {tokens.map((token, i) => (
                <span
                  key={i}
                  className={`${TOKEN_COLORS[token.type]} ${token.type !== 'plain' ? 'rounded transition-colors duration-200' : ''}`}
                >
                  {token.text}
                </span>
              ))}
              {"\u200B"}
            </div>

            {/* Actual Textarea (transparent text, positioned over highlight) */}
            <textarea
              ref={inputRef}
              rows={1}
              value={value}
              onChange={e => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onScroll={handleScroll}
              disabled={isLoading}
              placeholder="Every Monday at 9 AM, sync my GitHub PRs to Notion..."
              className="
                w-full bg-transparent text-transparent caret-black dark:caret-white placeholder-gray-400 dark:placeholder-gray-500
                focus:outline-none focus:ring-0
                disabled:cursor-not-allowed
                relative z-10 prompt-textarea-shared
              "
            />
          </div>

          {/* Bottom Bar */}
          <div className="flex items-center justify-between px-5 md:px-6 pb-4 pt-1">
            {/* Left: Token Legend */}
            <div className="flex items-center gap-3 text-[10px] font-medium tracking-wide uppercase text-gray-400 dark:text-gray-500">
              {hasTokens && (
                <>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-600"></span>
                    Trigger
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-gray-500 dark:bg-gray-500"></span>
                    Action
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-gray-800 dark:bg-gray-400"></span>
                    Target
                  </span>
                </>
              )}
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onCancel}
                className="px-3 py-1.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!value.trim() || isLoading}
                className={`
                  px-5 py-2 rounded-xl text-sm font-semibold transition-all duration-200
                  flex items-center gap-2
                  ${value.trim() && !isLoading
                    ? 'bg-black text-white hover:bg-gray-800 shadow-md hover:shadow-lg transform hover:scale-[1.02] dark:bg-white dark:text-black dark:hover:bg-gray-200'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-800 dark:text-gray-650'
                  }
                `}
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-gray-400 border-t-white rounded-full animate-spin" />
                    Parsing...
                  </>
                ) : (
                  <>
                    <Icons.Zap className="w-4 h-4" />
                    Create Schedule
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SchedulePromptBar;
